<template>
  <div class="send-email-view">
    <!-- Back header (hidden when hosted inside a BaseDrawer, which has its own header) -->
    <div v-if="!hosted" class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Send email</h5>
    </div>

    <!-- Initial load: templates + email settings in parallel -->
    <div v-if="loadingTemplates" class="view-body" :class="{ 'view-body--hosted': hosted }" aria-busy="true">
      <div class="form-row">
        <span class="skel skel--line-sm mb-2"></span>
        <span class="skel skel--field"></span>
      </div>
      <div class="form-row">
        <span class="skel skel--line-sm mb-2"></span>
        <span class="skel skel--field"></span>
      </div>
    </div>

    <!-- Nothing to send -->
    <div v-else-if="!hasAnyOption" class="view-body" :class="{ 'view-body--hosted': hosted }">
      <div class="empty-state">
        <i class="tim-icons icon-email-85 empty-icon"></i>
        <p class="empty-title">Nothing to send yet</p>
        <p class="empty-sub">
          Turn on a quick-send email in your automated email settings, or create a custom template to send branded emails.
        </p>
        <div class="empty-actions">
          <a
            href="/mail/automated/settings"
            target="_blank"
            rel="noopener"
            class="empty-action empty-action--ghost"
          >
            <i class="tim-icons icon-settings mr-1"></i> Automated emails
          </a>
          <a
            href="/mail/custom/landing?count=10&page=1&tab=0"
            target="_blank"
            rel="noopener"
            class="empty-action"
          >
            <i class="tim-icons icon-simple-add mr-1"></i> Custom templates
          </a>
        </div>
      </div>
    </div>

    <!-- Form -->
    <div v-else class="view-body" :class="{ 'view-body--hosted': hosted }">
      <!-- Picker (grouped: Quick send + Custom templates) -->
      <div class="form-row">
        <label class="form-label">Email</label>
        <!-- autocomplete="off" + a non-email-flavored placeholder defeats
             Chrome's saved-email autofill (which kicks in on any field whose
             placeholder/name contains "email"). name + role hints belt-and-
             suspenders the same. -->
        <el-select
          v-model="selectedOption"
          value-key="key"
          class="select-primary w-100"
          placeholder="Pick what to send"
          filterable
          autocomplete="off"
          name="comm-action-picker"
        >
          <el-option-group
            v-if="simpleOptions.length > 0"
            label="Simple emails"
          >
            <el-option
              v-for="opt in simpleOptions"
              :key="opt.key"
              :label="opt.name"
              :value="opt"
              :disabled="opt.disabled"
              class="select-primary"
            >
              <div class="option-row">
                <span class="option-name">{{ opt.name }}</span>
                <span v-if="opt.disabledReason" class="option-meta option-meta--muted">
                  {{ opt.disabledReason }}
                </span>
              </div>
            </el-option>
          </el-option-group>
          <el-option-group
            v-if="customOptions.length > 0"
            label="Custom templates"
          >
            <el-option
              v-for="opt in customOptions"
              :key="opt.key"
              :label="opt.name"
              :value="opt"
              class="select-primary"
            />
          </el-option-group>
        </el-select>
        <p v-if="errors.option" class="field-error">{{ errors.option }}</p>
      </div>

      <!-- Simple email description -->
      <info-hint v-if="isSimpleSelected" icon="icon-bell-55">
        <strong>{{ selectedOption.name }}</strong>
        <div class="simple-email-hint__description">{{ selectedOption.description }}</div>
      </info-hint>

      <!-- Custom template preview -->
      <div v-if="isCustomSelected" class="preview-block">
        <div class="preview-row">
          <span class="preview-label">Subject</span>
          <span class="preview-value">{{ selectedOption.subject_line || '—' }}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">From</span>
          <span class="preview-value">
            {{ selectedOption.from_name || companyGroupName }}
            <span v-if="selectedOption.from_email" class="preview-secondary">
              &lt;{{ selectedOption.from_email }}&gt;
            </span>
          </span>
        </div>
        <div v-if="selectedOption.type" class="preview-row">
          <span class="preview-label">Type</span>
          <span class="preview-value">{{ formatTemplateType(selectedOption.type) }}</span>
        </div>
        <div v-if="selectedOption.description" class="preview-row">
          <span class="preview-label">Description</span>
          <span class="preview-value">{{ selectedOption.description }}</span>
        </div>
      </div>

      <!-- Recipient -->
      <div class="form-row">
        <label class="form-label">Recipient</label>
        <input
          type="email"
          class="form-control recipient-input"
          :class="{ 'has-error': errors.recipient, 'is-readonly': recipientLocked }"
          :value="displayedRecipient"
          @input="recipient = $event.target.value"
          :disabled="recipientLocked"
          placeholder="customer@example.com"
        />
        <p v-if="errors.recipient" class="field-error">{{ errors.recipient }}</p>
        <p v-else-if="recipientLocked" class="field-hint">
          <i class="tim-icons icon-lock-circle mr-1"></i>
          Sent to the address on file for this email. Use a custom template to send to a different address.
        </p>
        <p v-else-if="recipientOverridden" class="field-hint warning">
          <i class="tim-icons icon-alert-circle-exc mr-1"></i>
          You're sending to a different address than the booking customer.
        </p>
      </div>

      <!-- Payment request inline warning -->
      <div v-if="showDueWarning" class="warning-band">
        <i class="tim-icons icon-money-coins mr-2"></i>
        <span>
          This will request payment of <strong>{{ formattedDue }}</strong> from the customer.
        </span>
      </div>

      <!-- Actions -->
      <div v-if="!hosted" class="send-actions">
        <base-button
          type="secondary"
          size="sm"
          :disabled="sending"
          @click="$emit('back')"
        >
          Cancel
        </base-button>
        <base-button
          type="primary"
          size="sm"
          :disabled="!canSend || sending"
          @click="send"
        >
          <span v-if="sending">Sending...</span>
          <span v-else>Send email</span>
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import { buildSimpleEmailOptions } from '@/util/emailSendOptions';
import { formatCurrency, getGroupSettings } from '@/util/currencyFormat';
import { formattedDate, time } from '@/plugins/dateFormatter';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Quick-send options are built by src/util/emailSendOptions.js so both the
// booking drawer and the transaction details drawer share one rule set.

export default {
  name: 'SendEmailView',

  props: {
    booking: { type: Object, default: () => ({}) },
    companyId: { type: [Number, String], default: null },
    companyGroupId: { type: [Number, String], default: null },
    currencySymbol: { type: String, default: '$' },
    // Group-level automated-email settings (`[{ email_type, is_active }, ...]`).
    // Provided by the parent from drawerData.email_settings — group-scoped
    // config that rides along with the drawer GET, so we don't need a separate
    // fetch here. Drives which Quick-send options appear in the picker.
    emailSettings: { type: Array, default: () => [] },
    // 'booking' (default): one booking, payloads scoped to it.
    // 'transaction': aggregated across `rows` for the transaction page.
    context: {
      type: String,
      default: 'booking',
      validator: v => v === 'booking' || v === 'transaction'
    },
    // Transaction context only: { id, email, status, due }.
    transaction: { type: Object, default: null },
    // Transaction context only: the page's mixed rows (bookings, items, gift cards).
    rows: { type: Array, default: () => [] },
    // BaseDrawer hosts render their own header, padding, and footer. When
    // hosted, the back header, body padding, and inline action buttons are
    // dropped and the host drives send() from its footer using the `state`
    // event ({ canSend, sending, hasAnyOption }).
    hosted: { type: Boolean, default: false }
  },

  emits: ['back', 'sent', 'state'],

  data() {
    return {
      emailTemplates: [],
      selectedOption: null,
      recipient: '',
      sending: false,
      loadingTemplates: false,
      errors: {
        option: '',
        recipient: ''
      },
      companyGroupName: ''
    };
  },

  computed: {
    isTransactionContext() {
      return this.context === 'transaction';
    },

    defaultRecipient() {
      if (this.isTransactionContext) return this.transaction?.email || '';
      // currentBookingData exposes customer_email (master + per-child); fall
      // back to plain email for safety if upstream shape changes.
      return this.booking?.customer_email || this.booking?.email || '';
    },

    transactionId() {
      return this.isTransactionContext ? this.transaction?.id : this.booking?.transaction_id;
    },

    dueAmount() {
      const due = this.isTransactionContext ? this.transaction?.due : this.booking?.due;
      const n = parseFloat(due);
      return Number.isFinite(n) ? n : 0;
    },

    simpleOptions() {
      const group = getGroupSettings();
      return buildSimpleEmailOptions({
        context: this.context,
        booking: this.booking,
        transaction: this.transaction,
        rows: this.rows,
        emailSettings: this.emailSettings,
        formatDate: d => formattedDate(group.date_format, d),
        formatTime: t => time(group.time_format, t)
      });
    },

    customOptions() {
      return this.emailTemplates.map(t => ({
        ...t,
        kind: 'custom',
        key: `custom:${t.id}`
      }));
    },

    hasAnyOption() {
      return this.simpleOptions.length > 0 || this.customOptions.length > 0;
    },

    isSimpleSelected() {
      return this.selectedOption?.kind === 'simple';
    },

    isCustomSelected() {
      return this.selectedOption?.kind === 'custom';
    },

    recipientLocked() {
      return this.isSimpleSelected;
    },

    // Simple options carry their own recipient (gift card delivery goes to the
    // card recipient, everything else to the customer). Custom templates use
    // the editable field.
    displayedRecipient() {
      return this.recipientLocked ? (this.selectedOption.recipient || '') : this.recipient;
    },

    recipientOverridden() {
      if (this.recipientLocked) return false;
      const original = this.defaultRecipient.trim().toLowerCase();
      const entered = (this.recipient || '').trim().toLowerCase();
      return !!original && !!entered && original !== entered;
    },

    showDueWarning() {
      if (!this.selectedOption) return false;
      const isPaymentRequest =
        (this.isSimpleSelected && this.selectedOption.email_type === 'payment_request') ||
        (this.isCustomSelected && this.selectedOption.type === 'payment_request');
      return isPaymentRequest && this.dueAmount > 0;
    },

    formattedDue() {
      const group = getGroupSettings();
      return formatCurrency(this.dueAmount, {
        currency: group.currency,
        currencySymbol: this.currencySymbol,
        currencyFormat: group.currency_format,
        numberFormat: group.number_format
      });
    },

    hostState() {
      return {
        canSend: this.canSend,
        sending: this.sending,
        hasAnyOption: this.hasAnyOption,
        loading: this.loadingTemplates
      };
    },

    canSend() {
      if (!this.selectedOption || this.selectedOption.disabled) return false;
      if (this.isCustomSelected) {
        return EMAIL_REGEX.test((this.recipient || '').trim());
      }
      return true;
    }
  },

  watch: {
    hostState: {
      immediate: true,
      handler(state) {
        this.$emit('state', state);
      }
    }
  },

  async mounted() {
    this.recipient = this.defaultRecipient;
    try {
      const groupRaw = localStorage.getItem('group');
      if (groupRaw) {
        this.companyGroupName = (JSON.parse(groupRaw)?.name) || '';
      }
    } catch (e) {
      // ignore — fall back to empty display
    }
    await this.loadTemplates();
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    formatTemplateType(type) {
      if (!type) return '';
      const spaced = String(type).replace(/_/g, ' ');
      return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    },

    async loadTemplates() {
      this.loadingTemplates = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates?count=1000&offset=0&view=transaction`;
        const response = await axios.get(url, config);
        const templates = (response.data?.templates || [])
          .filter(t => t.status !== 'archived')
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        this.emailTemplates = templates;
      } catch (error) {
        console.error('Failed to load email templates:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to load email templates',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });
      } finally {
        this.loadingTemplates = false;
      }
    },

    validate() {
      let valid = true;
      this.errors.option = '';
      this.errors.recipient = '';

      if (!this.selectedOption) {
        this.errors.option = 'Pick an email to send.';
        valid = false;
      } else if (this.selectedOption.disabled) {
        this.errors.option = this.selectedOption.disabledReason || 'This option is not available right now.';
        valid = false;
      }

      if (this.isCustomSelected) {
        const r = (this.recipient || '').trim();
        if (!r) {
          this.errors.recipient = 'Recipient email is required.';
          valid = false;
        } else if (!EMAIL_REGEX.test(r)) {
          this.errors.recipient = 'Enter a valid email address.';
          valid = false;
        }
      }
      return valid;
    },

    async send() {
      if (!this.validate() || this.sending) return;
      if (this.isSimpleSelected && !this.selectedOption.recipient) {
        this.$notify({
          type: 'danger',
          message: 'This option has no recipient address, so it cannot be sent.',
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });
        return;
      }
      this.sending = true;
      try {
        if (this.isSimpleSelected) {
          await this.sendSimpleEmail();
        } else {
          await this.sendCustomEmail();
        }
        const target = this.isSimpleSelected ? this.selectedOption.recipient : this.recipient.trim();
        const label = this.selectedOption.name;
        this.$notify({
          type: 'success',
          message: target ? `"${label}" sent to ${target}` : `"${label}" sent`,
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });
        this.$emit('sent', {
          kind: this.selectedOption.kind,
          email_type: this.selectedOption.email_type || this.selectedOption.type,
          template_id: this.selectedOption.id || null,
          name: this.selectedOption.name,
          recipient_email: target || ''
        });
      } catch (error) {
        this.handleSendError(error);
      } finally {
        this.sending = false;
      }
    },

    async sendSimpleEmail() {
      const opt = this.selectedOption;
      const transactionId = this.transactionId;
      if (!transactionId) throw new Error('Missing transaction id');
      const token = await this.getAuthToken();
      const config = { headers: { Authorization: token } };
      const base = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${transactionId}`;
      // endpoint and body come from the option builder and mirror the
      // transaction page handlers exactly. The lambdas return 204 even on
      // empty payloads but silently send nothing, so the builder never emits
      // an option without a recipient.
      await axios.post(base + opt.endpoint, opt.body, config);
    },

    async sendCustomEmail() {
      const transactionId = this.transactionId;
      if (!transactionId) throw new Error('Missing transaction id');
      const token = await this.getAuthToken();
      const config = { headers: { Authorization: token } };
      const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${transactionId}/send-custom-email`;
      // Payload shape matches TransactionDetails.vue:5148-5152 — the lambda
      // requires template_id + recipient_email + transaction_id even though
      // transaction_id is also in the URL path. booking_id is an additive
      // hint for the booking-scoped merge-variable work flagged earlier
      // (lambda ignores it today; harmless to send).
      const body = {
        template_id: this.selectedOption.id,
        recipient_email: this.recipient.trim(),
        transaction_id: transactionId,
        booking_id: this.booking?.id || null
      };
      await axios.post(url, body, config);
    },

    handleSendError(error) {
      console.error('Failed to send email:', error);
      let message = 'Failed to send email. Please try again.';
      const raw = error?.response?.data;
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (parsed?.error) message = parsed.error;
        else if (typeof raw === 'string' && raw.length < 240) message = raw;
      } catch (e) {
        if (typeof raw === 'string' && raw.length < 240) message = raw;
      }
      this.$notify({
        type: 'danger',
        message,
        verticalAlign: 'top',
        horizontalAlign: 'right'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.send-email-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
}

.view-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.back-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.view-title {
  font-size: 14px;
  font-weight: 600;
  color: white;
}

.view-body {
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* BaseDrawer already pads and scrolls its body; avoid doubling either. */
.view-body--hosted {
  padding: 0;
  overflow: visible;
  flex: none;
}

.form-row {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;
}

.recipient-input {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: white;
  padding: 10px 12px;
  font-size: 13px;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }

  &:focus {
    outline: none;
    border-color: #e14eca;
  }

  &.has-error {
    border-color: #ec250d;
  }

  &.is-readonly,
  &:disabled {
    color: rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.03);
    cursor: not-allowed;
  }
}

.field-error {
  font-size: 12px;
  color: #ec250d;
  margin: 6px 0 0;
}

.field-hint {
  font-size: 12px;
  margin: 6px 0 0;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.55);

  &.warning {
    color: #ffba00;
  }
}

.simple-email-hint__description {
  margin-top: 2px;
}

.option-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.option-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.option-meta {
  font-size: 11px;
  flex-shrink: 0;

  &--muted {
    color: rgba(255, 255, 255, 0.4);
  }
}

.preview-block {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.preview-row {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  line-height: 1.4;
}

.preview-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 2px;
}

.preview-value {
  font-size: 13px;
  color: white;
  word-break: break-word;
}

.preview-secondary {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
}

.warning-band {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  background: rgba(255, 186, 0, 0.1);
  border: 1px solid rgba(255, 186, 0, 0.3);
  border-radius: 6px;
  padding: 10px 12px;
  color: #ffba00;
  font-size: 13px;
}

.send-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.empty-state {
  text-align: center;
  padding: 40px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.empty-icon {
  font-size: 32px;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 12px;
}

.empty-title {
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}

.empty-sub {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  margin-bottom: 16px;
  max-width: 320px;
}

.empty-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.empty-action {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  background: rgba(225, 78, 202, 0.15);
  color: #e14eca;
  border: 1px solid #e14eca;
  border-radius: 6px;
  font-size: 12px;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(225, 78, 202, 0.25);
    color: #e14eca;
    text-decoration: none;
  }

  &--ghost {
    background: transparent;
    border-color: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.7);

    &:hover {
      background: rgba(255, 255, 255, 0.06);
      color: white;
      border-color: rgba(255, 255, 255, 0.3);
    }
  }
}
</style>

<style lang="scss">
.white-content {
  .send-email-view .view-header {
    border-bottom-color: #e3e3e3;
  }

  .send-email-view .back-btn {
    background: #f5f6fa;
    border-color: #e3e3e3;
    color: #9A9A9A;

    &:hover {
      background: #e9ecef;
      color: #344675;
    }
  }

  .send-email-view .view-title {
    color: #344675;
  }

  .send-email-view .form-label {
    color: #344675;
  }

  .send-email-view .recipient-input {
    background: white;
    border-color: #e3e3e3;
    color: #344675;

    &::placeholder {
      color: rgba(52, 70, 117, 0.4);
    }

    &:focus {
      border-color: #e14eca;
    }

    &.is-readonly,
    &:disabled {
      color: rgba(52, 70, 117, 0.6);
      background: #f5f6fa;
    }
  }

  .send-email-view .field-hint {
    color: rgba(52, 70, 117, 0.65);

    &.warning {
      color: #b27300;
    }
  }

  .send-email-view .preview-block {
    background: #f5f6fa;
    border-color: #e3e3e3;
  }

  .send-email-view .preview-label {
    color: rgba(52, 70, 117, 0.6);
  }

  .send-email-view .preview-value {
    color: #344675;
  }

  .send-email-view .preview-secondary {
    color: rgba(52, 70, 117, 0.6);
  }

  .send-email-view .send-actions {
    border-top-color: #e3e3e3;
  }

  .send-email-view .warning-band {
    background: rgba(255, 186, 0, 0.14);
    border-color: rgba(255, 186, 0, 0.5);
    color: #b27300;
  }

  .send-email-view .empty-icon {
    color: rgba(52, 70, 117, 0.3);
  }

  .send-email-view .empty-title {
    color: #344675;
  }

  .send-email-view .empty-sub {
    color: rgba(52, 70, 117, 0.7);
  }

  .send-email-view .empty-action {
    background: rgba(225, 78, 202, 0.1);
    color: #e14eca;
    border-color: #e14eca;

    &:hover {
      background: rgba(225, 78, 202, 0.18);
      color: #e14eca;
    }

    &--ghost {
      background: transparent;
      border-color: #e3e3e3;
      color: rgba(52, 70, 117, 0.7);

      &:hover {
        background: #f5f6fa;
        color: #344675;
        border-color: #cdd0d6;
      }
    }
  }

  .send-email-view .option-meta--muted {
    color: rgba(52, 70, 117, 0.45);
  }
}
</style>
